/**
 * Primitivas compartidas por las dos variantes de UI.
 *
 * La firma visual de la app es `<Pila>`: toda proporción se dibuja como placas discretas, nunca
 * como una barra continua. El motivo no es estético: en este dominio las cantidades ESTÁN
 * cuantizadas —placas de 5 lb, sesiones enteras, días enteros de colchón— y la bioimpedancia
 * tiene ±3–5 puntos de error, así que una barra suave insinuaría una precisión que el dato no
 * tiene. Que se vean las unidades es honestidad, no decoración.
 */

import type { ReactNode } from 'react';
import { formatearDual, formatearNumero, type Unidad } from '@mg/core';

// ---------------------------------------------------------------------------
// La pila de placas
// ---------------------------------------------------------------------------

export type EstadoPlaca = 'llena' | 'parcial' | 'hueca' | 'ambar' | 'alerta' | 'vacia';

export interface PilaProps {
  estados: EstadoPlaca[];
  vertical?: boolean;
  /** Alto en px de la pila horizontal (o de cada placa en la vertical). */
  alto?: number;
  etiqueta?: string;
}

const CLASE_PLACA: Record<EstadoPlaca, string> = {
  llena: 'placa placa--llena',
  parcial: 'placa placa--parcial',
  hueca: 'placa placa--hueca',
  ambar: 'placa placa--ambar',
  alerta: 'placa placa--alerta',
  vacia: 'placa',
};

export function Pila({ estados, vertical = false, alto = 28, etiqueta }: PilaProps): ReactNode {
  return (
    <div
      className={vertical ? 'pila pila--vertical' : 'pila'}
      style={vertical ? undefined : { height: alto }}
      role="img"
      aria-label={etiqueta}
    >
      {estados.map((estado, i) => (
        <span
          key={i}
          className={CLASE_PLACA[estado]}
          style={vertical ? { height: alto } : undefined}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

/**
 * Convierte una proporción en estados de placa. Con `valor` mayor a 1 se llenan todas y las
 * sobrantes van en ámbar: es el caso de recomposición corporal (p-ratio > 100 %), que es el
 * mejor resultado posible y merece verse distinto, no cortado en 100.
 */
export function estadosDeRatio(
  valor: number | null,
  placas = 10,
  tono: 'llena' | 'ambar' | 'alerta' = 'llena',
): EstadoPlaca[] {
  if (valor === null || !Number.isFinite(valor)) return new Array(placas).fill('hueca');

  const exacto = Math.max(0, valor) * placas;
  const enteras = Math.floor(exacto + 1e-9);
  const fraccion = exacto - enteras;

  return Array.from({ length: placas }, (_, i) => {
    if (i < Math.min(enteras, placas)) return tono;
    if (i === enteras && fraccion > 0.15 && i < placas) return tono === 'llena' ? 'parcial' : tono;
    return 'hueca';
  });
}

/** Pila de un plan: usadas, pendientes que caben, y en riesgo de vencer. */
export function PilaDePlan({
  usadas,
  pendientes,
  enRiesgo,
  alto = 24,
}: {
  usadas: number;
  pendientes: number;
  enRiesgo: number;
  alto?: number;
}): ReactNode {
  const estados: EstadoPlaca[] = [
    ...new Array<EstadoPlaca>(usadas).fill('llena'),
    ...new Array<EstadoPlaca>(Math.max(0, pendientes - enRiesgo)).fill('hueca'),
    ...new Array<EstadoPlaca>(enRiesgo).fill('alerta'),
  ];
  return (
    <Pila
      estados={estados}
      alto={alto}
      etiqueta={`${usadas} sesiones usadas, ${pendientes - enRiesgo} pendientes${
        enRiesgo > 0 ? `, ${enRiesgo} en riesgo de vencer` : ''
      }`}
    />
  );
}

// ---------------------------------------------------------------------------
// Valores medidos
// ---------------------------------------------------------------------------

/**
 * Un valor que el usuario leyó de una máquina. Va en monoespaciada y con las DOS unidades,
 * como exige §3: primero la que vio en la placa, después la conversión.
 */
export function Peso({
  valor,
  unidad,
  dual = true,
  className = '',
}: {
  valor: number;
  unidad: Unidad;
  dual?: boolean;
  className?: string;
}): ReactNode {
  const otra: Unidad = unidad === 'kg' ? 'lb' : 'kg';
  return (
    <span className={`lectura ${className}`}>
      {formatearNumero(valor)} <span className="unidad">{unidad}</span>
      {dual && (
        <span className="apagado" style={{ fontSize: '0.8em' }}>
          {' · '}
          {formatearDual({ valor, unidad }).split(' · ')[1]?.replace(` ${otra}`, '')}
          <span className="unidad"> {otra}</span>
        </span>
      )}
    </span>
  );
}

export function Cifra({
  valor,
  unidad,
  decimales = 1,
  heroe = false,
}: {
  valor: number | null;
  unidad?: string;
  decimales?: number;
  heroe?: boolean;
}): ReactNode {
  return (
    <span className={heroe ? 'cifra-heroe' : 'cifra-grande'}>
      {valor === null ? '—' : formatearNumero(valor, decimales)}
      {unidad && <span className="unidad"> {unidad}</span>}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Avisos e insignias
// ---------------------------------------------------------------------------

export type TonoAviso = 'importante' | 'atencion' | 'info' | 'neutro';

export function Aviso({
  tono = 'neutro',
  titulo,
  children,
}: {
  tono?: TonoAviso;
  titulo?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div className={tono === 'neutro' ? 'aviso' : `aviso aviso--${tono}`} role="note">
      {titulo && <strong style={{ display: 'block', marginBottom: 2 }}>{titulo}</strong>}
      {children}
    </div>
  );
}

export function Insignia({
  tono = 'neutro',
  children,
}: {
  tono?: 'neutro' | 'ambar' | 'agua' | 'alerta';
  children: ReactNode;
}): ReactNode {
  return <span className={tono === 'neutro' ? 'insignia' : `insignia insignia--${tono}`}>{children}</span>;
}

export function Vacio({ children }: { children: ReactNode }): ReactNode {
  return <div className="vacio">{children}</div>;
}

// ---------------------------------------------------------------------------
// Stepper de incrementos reales
// ---------------------------------------------------------------------------

/**
 * Sube y baja de a un incremento REAL de la máquina (§3). Los botones son del tamaño de un
 * objetivo táctil completo porque se tocan de pie, con una mano y con la otra ocupada.
 */
export function Stepper({
  valor,
  incremento,
  min = 0,
  onCambio,
  etiqueta,
  unidad,
}: {
  valor: number;
  incremento: number;
  min?: number;
  onCambio: (v: number) => void;
  etiqueta: string;
  unidad?: string;
}): ReactNode {
  const paso = (dir: number) => {
    const bruto = valor + dir * incremento;
    const redondeado = Math.round(bruto / incremento) * incremento;
    onCambio(Math.max(min, Number(redondeado.toFixed(4))));
  };

  return (
    <div>
      <span className="eyebrow etiqueta">{etiqueta}</span>
      <div className="stepper">
        <button
          type="button"
          className="boton"
          onClick={() => paso(-1)}
          aria-label={`Bajar ${incremento}`}
          disabled={valor <= min}
        >
          −
        </button>
        <input
          className="campo"
          inputMode="decimal"
          value={formatearNumero(valor, 2)}
          onChange={(e) => {
            const n = Number(e.target.value.replace(',', '.'));
            if (Number.isFinite(n)) onCambio(n);
          }}
          aria-label={etiqueta}
          style={{ textAlign: 'center', fontSize: 22, fontWeight: 650 }}
        />
        <button type="button" className="boton" onClick={() => paso(1)} aria-label={`Subir ${incremento}`}>
          +
        </button>
      </div>
      {unidad && (
        <p className="nota--fina" style={{ marginTop: 4, textAlign: 'center' }}>
          de {formatearNumero(incremento)} en {formatearNumero(incremento)} {unidad}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Iconos
// ---------------------------------------------------------------------------

export type NombreIcono =
  | 'casa'
  | 'registrar'
  | 'mancuerna'
  | 'balanza'
  | 'calendario'
  | 'camara'
  | 'progreso'
  | 'ajustes'
  | 'historial'
  | 'nube'
  | 'nube-tachada'
  | 'alerta'
  | 'check'
  | 'cerrar'
  | 'mas'
  | 'atras'
  | 'mobile'
  | 'desktop'
  | 'comida'
  | 'ayuda'
  | 'mas-destinos';

const RUTAS: Record<NombreIcono, ReactNode> = {
  casa: <path d="M3 10.5 12 3l9 7.5V21H3z" />,
  registrar: (
    <>
      <path d="M4 20h16" />
      <path d="M6 16V9M10 16V5M14 16v-6M18 16V7" />
    </>
  ),
  // El tab de Entrenar. Antes iba con `registrar`, que es un gráfico de barras: al lado del tab
  // Progreso (una línea que sube) eran dos gráficos, y el destino más usado de la app se leía como
  // una pantalla de análisis. Una mancuerna dice qué se hace ahí sin leer la etiqueta.
  mancuerna: (
    <>
      <rect x="2" y="7.5" width="4.5" height="9" rx="1.5" />
      <rect x="17.5" y="7.5" width="4.5" height="9" rx="1.5" />
      <path d="M6.5 12h11" />
    </>
  ),
  // Plataforma con dial y aguja. El rectángulo redondeado con una raya adentro que había antes no
  // decía nada —se leía como una tarjeta o como una puerta—; lo que hace reconocible a una balanza
  // es el dial, y la aguja en diagonal es lo que la separa de un enchufe.
  balanza: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="3" />
      <path d="M7.5 14a4.5 4.5 0 0 1 9 0" />
      <path d="M12 14l3-3" />
    </>
  ),
  calendario: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </>
  ),
  camara: (
    <>
      <path d="M4 8h3l1.5-2h7L17 8h3v12H4z" />
      <circle cx="12" cy="14" r="3.5" />
    </>
  ),
  progreso: (
    <>
      <path d="M4 18 9 12l4 3 7-8" />
      <path d="M15 7h5v5" />
    </>
  ),
  // Deslizadores y no un engranaje: a 18 px un engranaje se vuelve una manchita, y la primera
  // versión (círculo con rayos) se leía como un control de brillo.
  ajustes: (
    <>
      <path d="M3 7.5h9M17.5 7.5H21M3 16.5h5M13 16.5h8" />
      <circle cx="14.7" cy="7.5" r="2.3" />
      <circle cx="10.5" cy="16.5" r="2.3" />
    </>
  ),
  historial: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </>
  ),
  nube: <path d="M6 18h11a3.5 3.5 0 0 0 .3-7A5.5 5.5 0 0 0 6.6 9.4A4.3 4.3 0 0 0 6 18z" />,
  'nube-tachada': (
    <>
      <path d="M6 18h11a3.5 3.5 0 0 0 .3-7A5.5 5.5 0 0 0 6.6 9.4A4.3 4.3 0 0 0 6 18z" />
      <path d="M3 3l18 18" />
    </>
  ),
  alerta: (
    <>
      <path d="M12 3 2.5 20h19z" />
      <path d="M12 9v5M12 17h.01" />
    </>
  ),
  check: <path d="M4 12.5 9.5 18 20 6.5" />,
  cerrar: <path d="M5 5l14 14M19 5 5 19" />,
  mas: <path d="M12 5v14M5 12h14" />,
  atras: <path d="M15 5l-7 7 7 7" />,
  mobile: (
    <>
      <rect x="7" y="2.5" width="10" height="19" rx="2.5" />
      <path d="M11 18.5h2" />
    </>
  ),
  desktop: (
    <>
      <rect x="2.5" y="4" width="19" height="12" rx="2" />
      <path d="M9 20h6M12 16v4" />
    </>
  ),
  // Tenedor y cuchillo: se lee como "comida" a 22 px mejor que un plato, que a ese tamaño es un
  // círculo dentro de otro círculo.
  comida: (
    <>
      <path d="M7 3v7a2 2 0 0 0 2 2 2 2 0 0 0 2-2V3" />
      <path d="M9 12v9" />
      <path d="M16 3c1.6 1.2 2.4 3 2.4 5.2 0 1.6-.5 2.6-1.4 3.3V21" />
    </>
  ),
  ayuda: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.4a2.6 2.6 0 0 1 5 .8c0 1.7-2.5 2.1-2.5 3.6" />
      <path d="M12 17.3h.01" />
    </>
  ),
  // Tres puntos y no un hamburger: el hamburger promete un menú de la app entera, y esto abre
  // cuatro destinos concretos. Van rellenos porque a 22 px un círculo de radio 1,7 con contorno
  // se ve hueco y se lee como tres ceros.
  'mas-destinos': (
    <>
      <circle cx="5" cy="12" r="1.7" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.7" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1.7" fill="currentColor" stroke="none" />
    </>
  ),
};

export function Icono({ nombre, tamano }: { nombre: NombreIcono; tamano?: number }): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...(tamano ? { width: tamano, height: tamano } : {})}
    >
      {RUTAS[nombre]}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Campos
// ---------------------------------------------------------------------------

/**
 * Id derivado de la etiqueta. Se limpia todo lo que no sea alfanumérico: "Peso (kg)" daría
 * `c-peso-(kg)`, que es válido en HTML pero no es un selector CSS válido, y rompe cualquier
 * herramienta que intente encontrar el campo por id.
 */
function idDesde(prefijo: string, etiqueta: string): string {
  const limpio = etiqueta
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  return `${prefijo}-${limpio || 'campo'}`;
}

export function Campo({
  etiqueta,
  ayuda,
  revisar = false,
  texto = false,
  ...props
}: {
  etiqueta: string;
  ayuda?: string;
  revisar?: boolean;
  texto?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>): ReactNode {
  const id = idDesde('c', etiqueta);
  return (
    <div>
      <label className="eyebrow etiqueta" htmlFor={id}>
        {etiqueta}
        {revisar && (
          <span className="insignia insignia--ambar" style={{ marginLeft: 6 }}>
            revisar
          </span>
        )}
      </label>
      <input
        id={id}
        className={`campo${texto ? ' campo--texto' : ''}${revisar ? ' campo--revisar' : ''}`}
        {...props}
      />
      {ayuda && (
        <p className="nota--fina" style={{ marginTop: 4 }}>
          {ayuda}
        </p>
      )}
    </div>
  );
}

export function Selector({
  etiqueta,
  children,
  ...props
}: { etiqueta: string; children: ReactNode } & React.SelectHTMLAttributes<HTMLSelectElement>): ReactNode {
  const id = idDesde('s', etiqueta);
  return (
    <div>
      <label className="eyebrow etiqueta" htmlFor={id}>
        {etiqueta}
      </label>
      <select id={id} className="campo campo--texto" {...props}>
        {children}
      </select>
    </div>
  );
}
